import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Button,
    IconButton,
    Box,
    Typography,
    TextField,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import AddAccountCardModal from '../../../Widgets/AddAccountCardModal/AddAccountCardModal';
import EnumsService from '../../../../Services/enums.service';
import { toast } from 'react-toastify';

const MorningPaymentsImportModal = ({
    open,
    onClose,
    caseId,
    accountantId,
    token,
    onSuccess,
    previewMorningPayments,
    importMorningPayments,
    sortCodeIdOptions = [],
    accountCardOptions: accountCardOptionsProp = [],
    client,
    palCodes = [],
    fetchEnums,
    setLoading,
    setAccountCardOptions,
    t,
    direction = 'rtl',
}) => {
    const [file, setFile] = useState(null);
    const [defaultSortCodeId, setDefaultSortCodeId] = useState('3');
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [paymentTypeToCardId, setPaymentTypeToCardId] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showAddAccountCardModal, setShowAddAccountCardModal] = useState(false);
    const [addCardEditedItem, setAddCardEditedItem] = useState(null);
    const [localAccountCardOptions, setLocalAccountCardOptions] = useState([]);

    const accountCardOptions = localAccountCardOptions.length > 0 ? localAccountCardOptions : accountCardOptionsProp;

    useEffect(() => {
        if (!open || !caseId || !token) return;
        const effectiveCaseId = client?.caseId ?? caseId;
        let cancelled = false;
        (async () => {
            try {
                const res = await EnumsService.GetAccountCardsByCaseId(effectiveCaseId, token);
                if (cancelled || !res?.ok) return;
                const data = await res.json();
                setLocalAccountCardOptions([{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])]);
                if (setAccountCardOptions) {
                    setAccountCardOptions([{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])]);
                }
            } catch (e) {
                if (!cancelled) console.error('Fetch account cards failed:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [open, caseId, client?.caseId, token, t, setAccountCardOptions]);

    const cardsWithoutAddItem = useMemo(
        () => (accountCardOptions || []).filter((c) => c.value1 !== 'AddItem'),
        [accountCardOptions]
    );

    const getAccountName = useCallback(
        (code) => {
            if (code == null || code === '') return '';
            const c = cardsWithoutAddItem.find(
                (card) =>
                    String(card.externalSoftwareCardNumber || card.value1 || card.cardId || '') === String(code)
            );
            return c?.name ?? code;
        },
        [cardsWithoutAddItem]
    );

    const sortCodeLabel = useMemo(() => {
        const opt = (sortCodeIdOptions || []).find(
            (o) => String(o.sortCodeId) === String(defaultSortCodeId)
        );
        return opt ? `${opt.sortCodeId} - ${opt.sortCodeName ?? ''}` : defaultSortCodeId;
    }, [sortCodeIdOptions, defaultSortCodeId]);

    const runPreview = useCallback(async () => {
        if (!file || !caseId || !accountantId || !token || !previewMorningPayments) return;
        try {
            setProcessing(true);
            const res = await previewMorningPayments(caseId, accountantId, file, token);
            setPreviewData({
                entries: res.entries ?? [],
                distinctPaymentTypes: res.distinctPaymentTypes ?? [],
                missingAccountCards: res.missingAccountCards ?? [],
                errorMessages: res.errorMessages ?? [],
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
            });
            setPage(0);
            setPaymentTypeToCardId((prev) => {
                const next = { ...prev };
                (res.distinctPaymentTypes ?? []).forEach((pt) => {
                    if (next[pt] == null) next[pt] = '';
                });
                return next;
            });
        } catch (err) {
            console.error('Morning Payments preview failed:', err);
            setPreviewData({
                entries: [],
                distinctPaymentTypes: [],
                missingAccountCards: [],
                errorMessages: [err?.message || 'Preview failed'],
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
            });
        } finally {
            setProcessing(false);
        }
    }, [file, caseId, accountantId, token, previewMorningPayments]);

    useEffect(() => {
        if (open && file && caseId && accountantId && token && previewMorningPayments) {
            runPreview();
        } else if (!file) {
            setPreviewData(null);
        }
    }, [open, file, caseId, accountantId]);

    const handleChangeAfterAdd = useCallback(async () => {
        const effectiveCaseId = client?.caseId ?? caseId;
        if (effectiveCaseId && token) {
            try {
                const res = await EnumsService.GetAccountCardsByCaseId(effectiveCaseId, token);
                if (res?.ok) {
                    const data = await res.json();
                    const next = [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])];
                    setLocalAccountCardOptions(next);
                    if (setAccountCardOptions) setAccountCardOptions(next);
                }
            } catch (e) {
                console.error('Refresh account cards after add:', e);
            }
        }
        if (fetchEnums) await fetchEnums();
        setShowAddAccountCardModal(false);
        runPreview();
    }, [client?.caseId, caseId, token, setAccountCardOptions, t, fetchEnums, runPreview]);

    const handleDrop = (files) => {
        if (files?.length) setFile(files[0]);
        setPreviewData(null);
        setPaymentTypeToCardId({});
    };

    const handlePreview = () => {
        runPreview();
    };

    const handleCommit = async () => {
        if (!file || !caseId || !token || !importMorningPayments) return;
        const distinctTypes = previewData?.distinctPaymentTypes ?? [];
        const allMapped = distinctTypes.every((pt) => paymentTypeToCardId[pt]);
        if (!allMapped) {
            toast.warning(t('MapAllPaymentTypes') || 'Please map a debit account for each payment type.');
            return;
        }
        const mapping = {};
        distinctTypes.forEach((pt) => {
            const cardId = paymentTypeToCardId[pt];
            if (cardId) mapping[pt] = cardId;
        });
        try {
            setProcessing(true);
            const res = await importMorningPayments(caseId, file, mapping, defaultSortCodeId || '3', token);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const messages = res?.errorMessages ?? [];
            const duplicateCount = res?.duplicateCount ?? 0;
            const duplicateMessages = res?.duplicateMessages ?? [];
            if (errors > 0 && messages.length > 0) {
                toast.error(`${t('ImportFailed')} – ${t('processed')}: ${processed}, ${t('Errors')}: ${errors}. ${messages.slice(0, 3).join(' ')}`);
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                let msg = processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful');
                if (duplicateCount > 0) {
                    msg += `. ${duplicateCount} ${t('DuplicatesSkipped') || 'duplicates skipped'}`;
                    if (duplicateMessages.length > 0) {
                        duplicateMessages.slice(0, 3).forEach((m) => toast.info(m));
                    }
                }
                toast.success(msg);
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Morning Payments import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData(null);
        setPaymentTypeToCardId({});
        setPage(0);
        setRowsPerPage(10);
        onClose();
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const entries = previewData?.entries ?? [];
    const previewSummaries = useMemo(() => {
        const valid = entries.filter((e) => e.isValid);
        const sumAmount = valid.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        const hasPrm = valid.some((e) => e.gross != null || e.net != null || e.vat != null);
        const sumGross = hasPrm ? valid.reduce((s, e) => s + (Number(e.gross) ?? Number(e.amount) ?? 0), 0) : null;
        const sumNet = hasPrm ? valid.reduce((s, e) => s + (Number(e.net) ?? 0), 0) : null;
        const sumVat = hasPrm ? valid.reduce((s, e) => s + (Number(e.vat) ?? 0), 0) : null;
        return { sumAmount, sumGross, sumNet, sumVat, hasPrm };
    }, [entries]);

    if (!open) return null;

    const missingAccountCards = previewData?.missingAccountCards ?? [];
    const distinctPaymentTypes = previewData?.distinctPaymentTypes ?? [];
    const hasMissingCards = missingAccountCards.length > 0;
    const allPaymentTypesMapped = distinctPaymentTypes.every((pt) => paymentTypeToCardId[pt]);
    const paginatedEntries = entries.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const debitOptions = cardsWithoutAddItem.map((c) => ({
        ...c,
        cardId: c.cardId ?? c.value1,
        label: `${c.name ?? ''} -  ${c.cardId ?? ''}`.trim(),
    }));

    return (
        <>
            <div className="download-modal-overlay" onClick={() => { if (!showAddAccountCardModal) handleClose(); }}>
                <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                    <div
                        className={`download-modal-content ${direction}`}
                        style={{
                            position: 'relative',
                            maxWidth: previewData ? '1100px' : '560px',
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <h4 className="report-title">
                                {t('MorningPayments') || 'Morning Payments'} – {t('Preview')} & {t('Import')}
                            </h4>
                            <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={2}>
                            <div>
                                <h5>.csv</h5>
                                <DropzoneArea
                                    key={file ? file.name : 'morning-payments-empty'}
                                    onDrop={handleDrop}
                                    accept=".csv"
                                />
                                {file && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {t('FileSelected')}: {file.name}
                                    </Typography>
                                )}
                            </div>

                            {sortCodeIdOptions?.length > 0 ? (
                                <Autocomplete
                                    options={sortCodeIdOptions}
                                    getOptionLabel={(option) =>
                                        `${option.sortCodeId} - ${option.sortCodeName ?? ''}`
                                    }
                                    value={
                                        sortCodeIdOptions.find(
                                            (option) => String(option.sortCodeId) === String(defaultSortCodeId)
                                        ) || null
                                    }
                                    onChange={(event, newValue) => {
                                        const newSortCodeId = newValue ? String(newValue.sortCodeId) : '3';
                                        setDefaultSortCodeId(newSortCodeId);
                                        setPreviewData(null);
                                    }}
                                    size="small"
                                    sx={{ maxWidth: 280 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('DefaultSortCode') || 'Default sort code'}
                                            variant="standard"
                                        />
                                    )}
                                />
                            ) : (
                                <TextField
                                    label={t('DefaultSortCode') || 'Default sort code'}
                                    value={defaultSortCodeId}
                                    onChange={(e) => {
                                        setDefaultSortCodeId(e.target.value);
                                        setPreviewData(null);
                                    }}
                                    size="small"
                                    sx={{ maxWidth: 280 }}
                                    variant="standard"
                                />
                            )}

                            <Box display="flex" gap={1.5} mt={1}>
                                <Button
                                    variant="contained"
                                    onClick={handlePreview}
                                    disabled={!file || processing}
                                >
                                    {processing && !previewData ? t('Processing') : t('Preview')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleCommit}
                                    disabled={
                                        !previewData ||
                                        processing ||
                                        !allPaymentTypesMapped ||
                                        (previewData?.validCount ?? 0) === 0
                                    }
                                >
                                    {processing && previewData ? t('Processing') : t('Import')}
                                </Button>
                            </Box>
                        </Box>

                        {previewData && distinctPaymentTypes.length > 0 && (
                            <Box mt={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                    {t('PaymentTypeMapping') || 'Payment type → Debit account'}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={2}>
                                    {distinctPaymentTypes.map((paymentType) => (
                                        <Autocomplete
                                            key={paymentType}
                                            options={debitOptions}
                                            getOptionLabel={(option) => option.label || option.cardId || ''}
                                            value={
                                                debitOptions.find(
                                                    (opt) => String(opt.cardId) === String(paymentTypeToCardId[paymentType] ?? '')
                                                ) || null
                                            }
                                            onChange={(event, newValue) => {
                                                setPaymentTypeToCardId((prev) => ({
                                                    ...prev,
                                                    [paymentType]: newValue ? String(newValue.cardId) : '',
                                                }));
                                            }}
                                            size="small"
                                            sx={{ minWidth: 220 }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={paymentType}
                                                    variant="outlined"
                                                />
                                            )}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {previewData && (
                            <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t('TotalLines') || 'Total lines'}: <strong>{previewData.totalLines}</strong>
                                    {' | '}
                                    {t('ValidCount') || 'Valid'}: <strong>{previewData.validCount}</strong>
                                    {' | '}
                                    {t('ErrorCount') || 'Errors'}: <strong>{previewData.errorCount}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t('SumAmount') || 'Sum Amount'}: <strong>{formatAmount(previewSummaries.sumAmount)}</strong>
                                    {previewSummaries.hasPrm && previewSummaries.sumGross != null && (
                                        <> | {t('MoveinGross') || 'Gross'}: <strong>{formatAmount(previewSummaries.sumGross)}</strong></>
                                    )}
                                    {previewSummaries.hasPrm && previewSummaries.sumNet != null && (
                                        <> | {t('MoveinNet') || 'Net'}: <strong>{formatAmount(previewSummaries.sumNet)}</strong></>
                                    )}
                                    {previewSummaries.hasPrm && previewSummaries.sumVat != null && (
                                        <> | {t('MoveinVat') || 'VAT'}: <strong>{formatAmount(previewSummaries.sumVat)}</strong></>
                                    )}
                                </Typography>

                                {hasMissingCards && (
                                    <Box
                                        mb={2}
                                        p={1.5}
                                        bgcolor="info.light"
                                        borderRadius={1}
                                        sx={{ maxHeight: 220, overflow: 'auto' }}
                                    >
                                        <Typography variant="subtitle2" color="info.dark" sx={{ mb: 0.5 }}>
                                            {t('MissingAccountCards') || 'Missing account cards'} ({missingAccountCards.length})
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {t('MissingAccountCardsWillBeCreatedOnImport') || 'These will be created automatically on import (CardId = max + 1). You can still add them manually below if needed.'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                            {missingAccountCards.map((code, i) => (
                                                <Button
                                                    key={i}
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => {
                                                        setAddCardEditedItem({ accountCodeId: code });
                                                        setShowAddAccountCardModal(true);
                                                    }}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {code}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {previewData.errorMessages?.length > 0 && (
                                    <Box
                                        mb={2}
                                        p={1.5}
                                        bgcolor="error.light"
                                        borderRadius={1}
                                        sx={{ maxHeight: 220, overflow: 'auto' }}
                                    >
                                        <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                            {t('Errors') || 'Errors'} ({previewData.errorMessages.length})
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                            {previewData.errorMessages.map((msg, i) => (
                                                <li key={i}>
                                                    <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {msg}
                                                    </Typography>
                                                </li>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {entries.length > 0 && (
                                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line') || 'Line'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentDate') || 'Date'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('UploadDate') || 'Upload'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Description')}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('PaymentType') || 'Payment type'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Client') || 'Client'}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('Amount')}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DebitAccount') || 'Debit'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status') || 'Status'}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paginatedEntries.map((row, i) => (
                                                    <TableRow
                                                        key={row.lineNumber ?? i}
                                                        sx={{ bgcolor: row.isValid ? 'transparent' : 'action.hover' }}
                                                    >
                                                        <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>{row.documentDate}</TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>{row.uploadDate}</TableCell>
                                                        <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.description}>
                                                            {row.description ?? ''}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>{row.paymentType ?? '—'}</TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>{row.clientIdentifier ?? '—'}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.amount)}</TableCell>
                                                        <TableCell sx={{ fontSize: 12 }}>
                                                            {getAccountName(paymentTypeToCardId[row.paymentType]) || paymentTypeToCardId[row.paymentType] || '—'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {row.isValid ? (
                                                                <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                            ) : (
                                                                <Typography variant="body2" color="error.main" title={row.errorMessage}>
                                                                    {row.errorMessage ?? ''}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            component="div"
                                            count={entries.length}
                                            page={page}
                                            onPageChange={(_, p) => setPage(p)}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            rowsPerPageOptions={[10, 25, 50]}
                                            labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                        />
                                    </TableContainer>
                                )}
                            </Box>
                        )}
                    </div>
                </div>
            </div>

            {client && token && (
                <AddAccountCardModal
                    accountCardOptions={accountCardOptions}
                    open={showAddAccountCardModal}
                    onClose={() => { setShowAddAccountCardModal(false); setAddCardEditedItem(null); }}
                    editedItem={addCardEditedItem || {}}
                    isRange={false}
                    isClosedReportingMonth={false}
                    palCodes={palCodes}
                    propPalCode={null}
                    direction={direction}
                    client={client}
                    token={token}
                    setLoading={setLoading}
                    handleChangeAfterAdd={handleChangeAfterAdd}
                    fetchEnums={fetchEnums}
                    type="main"
                    isEdit={false}
                    accountCards={accountCardOptions}
                />
            )}
        </>
    );
};

export default MorningPaymentsImportModal;
